(() => {
  const WIDTH = 960;
  const HEIGHT = 540;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const random = (min, max) => min + Math.random() * (max - min);

  class FebeArcade {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-febe-game-modal]');
      this.player = root.querySelector('[data-febe-game-player]');
      this.canvas = root.querySelector('[data-febe-game-canvas]');
      this.ctx = this.canvas?.getContext('2d');
      this.overlay = root.querySelector('[data-febe-game-overlay]');
      this.title = root.querySelector('[data-febe-game-title]');
      this.overlayTitle = root.querySelector('[data-febe-game-overlay-title]');
      this.kicker = root.querySelector('[data-febe-game-kicker]');
      this.instructions = root.querySelector('[data-febe-game-instructions]');
      this.startButton = root.querySelector('[data-febe-game-start]');
      this.scoreElement = root.querySelector('[data-febe-game-score]');
      this.statLabel = root.querySelector('[data-febe-game-stat-label]');
      this.statElement = root.querySelector('[data-febe-game-stat]');
      this.bestElement = root.querySelector('[data-febe-game-best]');
      this.controlHint = root.querySelector('[data-febe-game-control-hint]');
      this.actionLabel = root.querySelector('[data-febe-game-action-label]');
      this.secondaryLabel = root.querySelector('[data-febe-game-secondary-label]');
      this.modeLabel = root.querySelector('[data-febe-game-mode]');
      this.preRoll = root.querySelector('[data-febe-game-preroll]');
      this.preRollAds = Array.from(root.querySelectorAll('[data-febe-preroll-ad]'));
      this.preRollPosition = root.querySelector('[data-febe-preroll-position]');
      this.preRollStatus = root.querySelector('[data-febe-preroll-status]');
      this.preRollNext = root.querySelector('[data-febe-preroll-next]');
      this.preRollContinue = root.querySelector('[data-febe-preroll-continue]');
      this.preRollButtonLabel = root.querySelector('[data-febe-preroll-button-label]');
      this.preRollCountdown = root.querySelector('[data-febe-preroll-countdown]');
      this.preRollProgress = root.querySelector('[data-febe-preroll-progress]');
      this.controls = new Set();
      this.score = 0;
      this.gameKey = 'ages';
      this.running = false;
      this.opened = false;
      this.lastTime = 0;
      this.animationFrame = null;
      this.pointerStart = null;
      this.lastTrigger = null;
      this.preRollTimer = null;
      this.preRollSequence = [];
      this.preRollDuration = 5000;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.meta = this.gameMetadata();
      this.loop = this.loop.bind(this);
      if (!this.canvas || !this.ctx || !this.modal) return;
      this.configureCanvas();
      this.bind();
    }

    gameMetadata() {
      return {
        ageofwar: { title: 'Age of War', kicker: 'Publisher-hosted · Strategy', instructions: 'Advance through five ages, command units and turrets, defend your base, and destroy the enemy.', controls: [], hint: 'Opens the official Max Games play page', stat: 'Ages', externalUrl: 'https://www.maxgames.com/play/age-of-war.html' },
        ages: { title: 'FEBE Ages', kicker: 'Strategy · Lane battle', instructions: 'Deploy guardians, defend your gate, and advance through three original eras.', controls: ['action', 'secondary'], action: 'Deploy', secondary: 'Evolve', hint: 'Space deploys · Shift evolves · Tap controls included', stat: 'Era' },
        runner: { title: 'Crown Runner', kicker: 'Arcade · Runner', instructions: 'Jump the skyline, avoid barriers, and collect gold crests.', controls: ['left', 'right', 'action'], action: 'Jump', hint: 'Arrow keys move · Space jumps · Tap controls included', stat: 'Distance' },
        hoops: { title: 'Royal Hoops', kicker: 'Sports · Timing', instructions: 'Release inside the moving gold zone and make the most of ten shots.', controls: ['action'], action: 'Shoot', hint: 'Press Space or tap Shoot at the perfect moment', stat: 'Shots' },
        goal: { title: 'Goal Rush', kicker: 'Sports · Accuracy', instructions: 'Time the moving aim marker, read the keeper, and place eight kicks.', controls: ['action'], action: 'Kick', hint: 'Press Space or tap Kick to place the shot', stat: 'Kicks' },
        breakout: { title: 'Gold Breaker', kicker: 'Classic · Breakout', instructions: 'Move the paddle, launch the ball, and clear every gold block.', controls: ['left', 'right', 'action'], action: 'Launch', hint: 'Arrow keys move · Space launches · Touch controls included', stat: 'Lives' },
        snake: { title: 'Lion Trail', kicker: 'Classic · Trail', instructions: 'Guide the trail toward each star without touching the walls or yourself.', controls: ['left', 'right', 'up', 'down'], hint: 'Use arrow keys or the touch direction pad', stat: 'Length' },
        memory: { title: 'Star Match', kicker: 'Puzzle · Memory', instructions: 'Tap cards to reveal and match all eight pairs in as few moves as possible.', controls: [], hint: 'Tap or click two cards to reveal a pair', stat: 'Moves' },
        lake: { title: 'Lake Escape', kicker: 'Arcade · Dodge', instructions: 'Glide through the current, avoid hazards, and collect bright stars.', controls: ['left', 'right', 'up', 'down'], hint: 'Use arrow keys or the touch direction pad', stat: 'Lives' },
        tower: { title: 'Tower of Purpose', kicker: 'Timing · Builder', instructions: 'Drop each moving floor onto the foundation and build as high as you can.', controls: ['action'], action: 'Drop', hint: 'Press Space or tap Drop when the floor is aligned', stat: 'Floors' },
        focus: { title: 'Focus Flash', kicker: 'Reflex · Reaction', instructions: 'Wait for the screen to turn green, then react. Going early costs a chance.', controls: ['action'], action: 'React', hint: 'Press Space or tap React only after the green flash', stat: 'Round' },
        merge: { title: 'FEBE Merge', kicker: 'Puzzle · Merge', instructions: 'Slide matching tiles together and build from Find toward Experience.', controls: ['left', 'right', 'up', 'down'], hint: 'Use arrow keys or swipe across the game area', stat: 'Largest' },
      };
    }

    configureCanvas() {
      const ratio = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
      this.pixelRatio = ratio;
      this.canvas.width = WIDTH * ratio;
      this.canvas.height = HEIGHT * ratio;
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    bind() {
      this.root.querySelectorAll('[data-febe-game-open]').forEach((button) => {
        button.addEventListener('click', () => this.open(button.dataset.febeGameOpen, button), { signal: this.signal });
      });
      this.root.querySelectorAll('[data-febe-game-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal: this.signal });
      });
      this.startButton?.addEventListener('click', () => this.start(), { signal: this.signal });
      this.preRollContinue?.addEventListener('click', () => this.finishPreRoll(), { signal: this.signal });
      this.root.querySelector('[data-febe-game-restart]')?.addEventListener('click', () => this.start(), { signal: this.signal });

      this.root.querySelectorAll('[data-febe-game-control]').forEach((button) => {
        const action = button.dataset.febeGameControl;
        button.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          button.setPointerCapture?.(event.pointerId);
          button.classList.add('is-active');
          this.control(action, true);
        }, { signal: this.signal });
        ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => {
          button.addEventListener(name, () => {
            button.classList.remove('is-active');
            this.control(action, false);
          }, { signal: this.signal });
        });
        button.addEventListener('click', (event) => {
          if (event.detail !== 0) return;
          this.control(action, true);
          this.control(action, false);
        }, { signal: this.signal });
      });

      document.addEventListener('keydown', (event) => this.onKey(event, true), { signal: this.signal });
      document.addEventListener('keyup', (event) => this.onKey(event, false), { signal: this.signal });
      this.canvas.addEventListener('pointerdown', (event) => this.onCanvasPointerDown(event), { signal: this.signal });
      this.canvas.addEventListener('pointermove', (event) => this.onCanvasPointerMove(event), { signal: this.signal });
      this.canvas.addEventListener('pointerup', (event) => this.onCanvasPointerUp(event), { signal: this.signal });
      this.canvas.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal: this.signal });
      window.addEventListener('resize', () => {
        this.configureCanvas();
        if (this.opened) this.draw();
      }, { signal: this.signal });
    }

    open(key, trigger) {
      if (!this.meta[key]) return;
      this.lastTrigger = trigger;
      this.gameKey = key;
      this.opened = true;
      this.running = false;
      this.modal.hidden = false;
      document.body.classList.add('febe-arcade-open');
      this.applyMetadata();
      this.reset();
      this.showOverlay(this.meta[key].title, this.meta[key].instructions, 'Play now');
      this.startPreRoll();
      window.requestAnimationFrame(() => this.player?.focus());
    }

    close() {
      this.clearPreRollTimer();
      this.running = false;
      this.opened = false;
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.modal.hidden = true;
      if (this.preRoll) this.preRoll.hidden = true;
      this.player?.classList.remove('is-preroll');
      document.body.classList.remove('febe-arcade-open');
      this.controls.clear();
      this.lastTrigger?.focus();
    }

    applyMetadata() {
      const meta = this.meta[this.gameKey];
      if (this.title) this.title.textContent = meta.title;
      if (this.overlayTitle) this.overlayTitle.textContent = meta.title;
      if (this.kicker) this.kicker.textContent = meta.kicker;
      if (this.instructions) this.instructions.textContent = meta.instructions;
      if (this.statLabel?.firstChild) this.statLabel.firstChild.textContent = `${meta.stat} `;
      if (this.actionLabel) this.actionLabel.textContent = meta.action || 'Action';
      if (this.secondaryLabel) this.secondaryLabel.textContent = meta.secondary || 'Special';
      if (this.controlHint) this.controlHint.textContent = meta.hint;
      if (this.modeLabel) this.modeLabel.textContent = meta.externalUrl ? 'FEBE ARCADE · OFFICIAL GAME LINK' : 'FEBE ARCADE · PLAYING HERE';
      this.root.querySelectorAll('[data-febe-game-control]').forEach((button) => {
        button.hidden = !meta.controls.includes(button.dataset.febeGameControl);
      });
      this.best = this.readBest();
      if (this.bestElement) this.bestElement.textContent = String(this.best);
    }

    startPreRoll() {
      if (!this.preRoll || this.preRollAds.length === 0) {
        this.start();
        return;
      }

      this.clearPreRollTimer();
      this.running = false;
      this.player?.classList.add('is-preroll');
      this.preRoll.hidden = false;
      this.overlay.hidden = true;
      const meta = this.meta[this.gameKey];
      const rankedAds = this.rankPreRollAds(meta);
      this.preRollSequence = rankedAds.slice(0, Math.min(3, rankedAds.length));
      this.renderPreRollAd(0);

      if (this.preRollStatus) this.preRollStatus.textContent = 'Sponsor reel playing';
      if (this.preRollNext) this.preRollNext.textContent = meta.title;
      if (this.preRollContinue) this.preRollContinue.disabled = true;
      if (this.preRollButtonLabel) this.preRollButtonLabel.textContent = 'Continue in';
      if (this.preRollCountdown) {
        this.preRollCountdown.hidden = false;
        this.preRollCountdown.textContent = String(Math.ceil(this.preRollDuration / 1000));
      }
      if (this.preRollProgress) this.preRollProgress.style.transform = 'scaleX(0)';

      const startedAt = performance.now();
      let visibleIndex = 0;
      const updatePreRoll = () => {
        const elapsed = Math.max(0, performance.now() - startedAt);
        const progress = Math.min(1, elapsed / this.preRollDuration);
        const remaining = Math.max(0, Math.ceil((this.preRollDuration - elapsed) / 1000));
        const nextIndex = Math.min(
          this.preRollSequence.length - 1,
          Math.floor(progress * this.preRollSequence.length)
        );

        if (nextIndex !== visibleIndex && nextIndex >= 0) {
          visibleIndex = nextIndex;
          this.renderPreRollAd(visibleIndex);
        }
        if (this.preRollCountdown) this.preRollCountdown.textContent = String(remaining);
        if (this.preRollProgress) this.preRollProgress.style.transform = `scaleX(${progress})`;
        if (progress >= 1) this.completePreRoll();
      };

      updatePreRoll();
      this.preRollTimer = window.setInterval(updatePreRoll, 100);
    }

    rankPreRollAds(meta) {
      const gameKeywords = {
        ageofwar: 'strategy games',
        ages: 'strategy games',
        runner: 'fitness movement activewear',
        hoops: 'sports fitness movement',
        goal: 'sports fitness movement',
        breakout: 'games classic',
        snake: 'games classic',
        memory: 'games puzzle',
        lake: 'games fitness movement',
        tower: 'games builder',
        focus: 'fitness performance games',
        merge: 'games puzzle',
      };
      const context = `${meta.title} ${meta.kicker} ${meta.instructions} ${gameKeywords[this.gameKey] || 'games'} FEBE`
        .toLowerCase()
        .match(/[a-z0-9]+/g) || [];
      const contextWords = new Set(context);
      const ranked = this.preRollAds.map((ad, index) => {
        const keywords = (ad.dataset.febeAdKeywords || '').toLowerCase().match(/[a-z0-9]+/g) || [];
        const matches = keywords.reduce((total, keyword) => total + (contextWords.has(keyword) ? 1 : 0), 0);
        const priority = Number.parseFloat(ad.dataset.febeAdPriority || '0') || 0;
        return { ad, index, score: matches * 10 + priority };
      }).sort((left, right) => right.score - left.score || left.index - right.index);

      let cursor = 0;
      try {
        cursor = Number.parseInt(localStorage.getItem('febe-arcade-preroll-cursor') || '0', 10) || 0;
        localStorage.setItem('febe-arcade-preroll-cursor', String(cursor + 3));
      } catch (_error) { /* Rotation still works when local storage is unavailable. */ }
      if (!ranked.length) return [];
      const shift = ((cursor % ranked.length) + ranked.length) % ranked.length;
      return ranked.slice(shift).concat(ranked.slice(0, shift)).map((item) => item.ad);
    }

    renderPreRollAd(index) {
      this.preRollAds.forEach((ad) => {
        ad.hidden = true;
        ad.querySelectorAll('a').forEach((link) => { link.tabIndex = -1; });
      });
      const activeAd = this.preRollSequence[index];
      if (!activeAd) return;
      activeAd.hidden = false;
      activeAd.querySelectorAll('a').forEach((link) => { link.tabIndex = 0; });
      if (this.preRollPosition) this.preRollPosition.textContent = `Promotion ${index + 1} of ${this.preRollSequence.length}`;
    }

    completePreRoll() {
      this.clearPreRollTimer();
      const meta = this.meta[this.gameKey];
      if (this.preRollStatus) this.preRollStatus.textContent = 'Sponsor reel complete';
      if (this.preRollContinue) this.preRollContinue.disabled = false;
      if (this.preRollButtonLabel) this.preRollButtonLabel.textContent = meta.externalUrl ? 'Play Age of War on Max Games' : 'Start game';
      if (this.preRollCountdown) {
        this.preRollCountdown.hidden = true;
        this.preRollCountdown.textContent = '';
      }
      if (this.preRollProgress) this.preRollProgress.style.transform = 'scaleX(1)';
    }

    finishPreRoll() {
      if (!this.preRollContinue || this.preRollContinue.disabled) return;
      this.clearPreRollTimer();
      if (this.meta[this.gameKey]?.externalUrl) {
        this.launchExternalGame();
        return;
      }
      this.start();
    }

    launchExternalGame() {
      const meta = this.meta[this.gameKey];
      if (!meta?.externalUrl) return;
      const link = document.createElement('a');
      link.href = meta.externalUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.hidden = true;
      document.body.appendChild(link);
      link.click();
      link.remove();
      this.root.dispatchEvent(new CustomEvent('febe:game-launch', { bubbles: true, detail: { game: this.gameKey, destination: 'publisher' } }));
      this.close();
    }

    clearPreRollTimer() {
      if (this.preRollTimer) window.clearInterval(this.preRollTimer);
      this.preRollTimer = null;
    }

    start() {
      if (this.meta[this.gameKey]?.externalUrl) {
        this.launchExternalGame();
        return;
      }
      this.clearPreRollTimer();
      if (this.preRoll) this.preRoll.hidden = true;
      this.player?.classList.remove('is-preroll');
      window.cancelAnimationFrame(this.animationFrame);
      this.reset();
      this.overlay.hidden = true;
      this.running = true;
      this.lastTime = performance.now();
      this.canvas.focus({ preventScroll: true });
      if (this.gameKey === 'merge') {
        this.draw();
        this.updateHud();
      } else {
        this.animationFrame = window.requestAnimationFrame(this.loop);
      }
    }

    reset() {
      this.score = 0;
      this.controls.clear();
      switch (this.gameKey) {
        case 'ages': this.resetAges(); break;
        case 'runner': this.resetRunner(); break;
        case 'hoops': this.resetHoops(); break;
        case 'goal': this.resetGoal(); break;
        case 'breakout': this.resetBreakout(); break;
        case 'snake': this.resetSnake(); break;
        case 'memory': this.resetMemory(); break;
        case 'lake': this.resetLake(); break;
        case 'tower': this.resetTower(); break;
        case 'focus': this.resetFocus(); break;
        case 'merge': this.resetMerge(); break;
        default: break;
      }
      this.updateHud();
      this.draw();
    }

    loop(time) {
      if (!this.running || !this.opened) return;
      const dt = Math.min(0.034, Math.max(0, (time - this.lastTime) / 1000));
      this.lastTime = time;
      this.update(dt);
      this.draw();
      this.updateHud();
      if (this.running) this.animationFrame = window.requestAnimationFrame(this.loop);
    }

    update(dt) {
      switch (this.gameKey) {
        case 'ages': this.updateAges(dt); break;
        case 'runner': this.updateRunner(dt); break;
        case 'hoops': this.updateHoops(dt); break;
        case 'goal': this.updateGoal(dt); break;
        case 'breakout': this.updateBreakout(dt); break;
        case 'snake': this.updateSnake(dt); break;
        case 'memory': this.updateMemory(dt); break;
        case 'lake': this.updateLake(dt); break;
        case 'tower': this.updateTower(dt); break;
        case 'focus': this.updateFocus(dt); break;
        case 'merge': break;
        default: break;
      }
    }

    draw() {
      this.ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      this.ctx.clearRect(0, 0, WIDTH, HEIGHT);
      switch (this.gameKey) {
        case 'ages': this.drawAges(); break;
        case 'runner': this.drawRunner(); break;
        case 'hoops': this.drawHoops(); break;
        case 'goal': this.drawGoal(); break;
        case 'breakout': this.drawBreakout(); break;
        case 'snake': this.drawSnake(); break;
        case 'memory': this.drawMemory(); break;
        case 'lake': this.drawLake(); break;
        case 'tower': this.drawTower(); break;
        case 'focus': this.drawFocus(); break;
        case 'merge': this.drawMerge(); break;
        default: break;
      }
    }

    onKey(event, pressed) {
      if (!this.opened) return;
      if (event.key === 'Escape' && pressed) { this.close(); return; }
      const map = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down', ' ': 'action', Enter: 'action', Shift: 'secondary' };
      const action = map[event.key];
      if (!action || !this.meta[this.gameKey].controls.includes(action)) return;
      event.preventDefault();
      if (pressed && event.repeat && (action === 'action' || action === 'secondary')) return;
      this.control(action, pressed);
    }

    control(action, pressed) {
      if (!this.running) return;
      if (pressed) this.controls.add(action);
      else this.controls.delete(action);
      if (!pressed) return;
      if (['left', 'right', 'up', 'down'].includes(action)) this.directionAction(action);
      if (action === 'action') this.primaryAction();
      if (action === 'secondary') this.secondaryAction();
    }

    directionAction(direction) {
      if (this.gameKey === 'snake') this.snakeDirection(direction);
      if (this.gameKey === 'merge') this.moveMerge(direction);
    }

    primaryAction() {
      switch (this.gameKey) {
        case 'ages': this.deployAgesUnit(); break;
        case 'runner': this.jumpRunner(); break;
        case 'hoops': this.shootHoops(); break;
        case 'goal': this.shootGoal(); break;
        case 'breakout': this.launchBreakout(); break;
        case 'tower': this.dropTower(); break;
        case 'focus': this.reactFocus(); break;
        default: break;
      }
    }

    secondaryAction() {
      if (this.gameKey === 'ages') this.evolveAges();
    }

    canvasPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: (event.clientX - rect.left) * WIDTH / rect.width, y: (event.clientY - rect.top) * HEIGHT / rect.height };
    }

    onCanvasPointerDown(event) {
      if (!this.running) return;
      event.preventDefault();
      const point = this.canvasPoint(event);
      this.pointerStart = point;
      if (this.gameKey === 'memory') this.flipMemoryAt(point.x, point.y);
      else if (this.gameKey === 'breakout') this.breakout.paddleX = clamp(point.x, 70, 890);
      else if (this.gameKey === 'lake') { this.lake.player.x = point.x; this.lake.player.y = point.y; }
      else if (this.gameKey !== 'merge') this.primaryAction();
    }

    onCanvasPointerMove(event) {
      if (!this.running || !this.pointerStart) return;
      const point = this.canvasPoint(event);
      if (this.gameKey === 'breakout') this.breakout.paddleX = clamp(point.x, 70, 890);
      if (this.gameKey === 'lake') { this.lake.player.x = clamp(point.x, 30, 930); this.lake.player.y = clamp(point.y, 60, 500); }
    }

    onCanvasPointerUp(event) {
      if (!this.running || !this.pointerStart) return;
      const point = this.canvasPoint(event);
      if (this.gameKey === 'merge') {
        const dx = point.x - this.pointerStart.x;
        const dy = point.y - this.pointerStart.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) > 30) this.moveMerge(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up'));
      }
      this.pointerStart = null;
    }

    updateHud() {
      if (this.scoreElement) this.scoreElement.textContent = String(Math.max(0, Math.round(this.score)));
      if (this.statElement) this.statElement.textContent = String(this.currentStat());
    }

    currentStat() {
      switch (this.gameKey) {
        case 'ages': return this.ages.age;
        case 'runner': return `${Math.floor(this.runner.distance)}m`;
        case 'hoops': return `${this.hoops.shots}/10`;
        case 'goal': return `${this.goal.shots}/8`;
        case 'breakout': return this.breakout.lives;
        case 'snake': return this.snake.body.length;
        case 'memory': return this.memory.moves;
        case 'lake': return this.lake.lives;
        case 'tower': return this.tower.blocks.length - 1;
        case 'focus': return `${this.focus.rounds}/5`;
        case 'merge': return Math.max(...this.merge.grid);
        default: return 1;
      }
    }

    finish(message, success = false) {
      if (!this.running) return;
      this.running = false;
      this.writeBest();
      this.draw();
      this.updateHud();
      this.showOverlay(success ? 'Purpose achieved.' : 'Ready for another run?', message, 'Play again');
    }

    showOverlay(title, message, buttonLabel) {
      if (this.overlayTitle) this.overlayTitle.textContent = title;
      if (this.instructions) this.instructions.textContent = message;
      if (this.startButton) this.startButton.textContent = buttonLabel;
      this.overlay.hidden = false;
    }

    readBest() {
      try { return Number.parseInt(localStorage.getItem(`febe-arcade-best-${this.gameKey}`) || '0', 10) || 0; }
      catch (_error) { return 0; }
    }

    writeBest() {
      const value = Math.max(this.best || 0, Math.round(this.score));
      this.best = value;
      if (this.bestElement) this.bestElement.textContent = String(value);
      try { localStorage.setItem(`febe-arcade-best-${this.gameKey}`, String(value)); }
      catch (_error) { /* High scores are optional when storage is blocked. */ }
    }

    gradient(top, bottom) {
      const gradient = this.ctx.createLinearGradient(0, 0, 0, HEIGHT);
      gradient.addColorStop(0, top);
      gradient.addColorStop(1, bottom);
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, WIDTH, HEIGHT);
    }

    rounded(x, y, width, height, radius, fill, stroke) {
      const r = Math.min(radius, width / 2, height / 2);
      const ctx = this.ctx;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + width - r, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + r);
      ctx.lineTo(x + width, y + height - r);
      ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
      ctx.lineTo(x + r, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      if (fill) { ctx.fillStyle = fill; ctx.fill(); }
      if (stroke) { ctx.strokeStyle = stroke; ctx.stroke(); }
    }

    label(text, x, y, size = 18, color = '#fff', align = 'left', weight = 650) {
      const ctx = this.ctx;
      ctx.fillStyle = color;
      ctx.font = `${weight} ${size}px system-ui, -apple-system, sans-serif`;
      ctx.textAlign = align;
      ctx.textBaseline = 'middle';
      ctx.fillText(String(text), x, y);
    }

    health(x, y, width, value, max, color) {
      this.rounded(x, y, width, 7, 4, 'rgba(255,255,255,.16)');
      this.rounded(x, y, width * clamp(value / max, 0, 1), 7, 4, color);
    }

    // FEBE Ages — original lane-defense game.
    resetAges() {
      this.ages = { playerBase: 100, enemyBase: 100, gold: 72, age: 1, units: [], enemies: [], spawn: 1.4, elapsed: 0 };
    }

    ageStats(side) {
      const age = this.ages.age;
      return side === 'player'
        ? { hp: 32 + age * 12, damage: 7 + age * 3, speed: 35 + age * 3, color: ['#d5b86c', '#63b28d', '#68b9c3'][age - 1] }
        : { hp: 27 + age * 10, damage: 6 + age * 2.6, speed: 31 + age * 2, color: '#a94b58' };
    }

    makeAgeUnit(side) {
      const stats = this.ageStats(side);
      return { side, x: side === 'player' ? 145 : 815, hp: stats.hp, maxHp: stats.hp, damage: stats.damage, speed: stats.speed, color: stats.color, cooldown: random(0, 0.45), size: 18 + this.ages.age * 2 };
    }

    deployAgesUnit() {
      const cost = 20 + this.ages.age * 6;
      if (this.ages.gold < cost || this.ages.units.length >= 8) return;
      this.ages.gold -= cost;
      this.ages.units.push(this.makeAgeUnit('player'));
    }

    evolveAges() {
      const cost = 105 + this.ages.age * 55;
      if (this.ages.age >= 3 || this.ages.gold < cost) return;
      this.ages.gold -= cost;
      this.ages.age += 1;
      this.score += 250;
    }

    updateAges(dt) {
      const game = this.ages;
      game.elapsed += dt;
      game.gold = Math.min(260, game.gold + dt * (9 + game.age * 2));
      game.spawn -= dt;
      if (game.spawn <= 0) {
        game.enemies.push(this.makeAgeUnit('enemy'));
        game.spawn = Math.max(1.2, 3.2 - game.elapsed / 65) * random(0.82, 1.18);
      }
      const all = [...game.units, ...game.enemies];
      all.forEach((unit) => {
        unit.cooldown -= dt;
        const foes = unit.side === 'player' ? game.enemies : game.units;
        const target = foes.filter((foe) => foe.hp > 0).sort((a, b) => Math.abs(a.x - unit.x) - Math.abs(b.x - unit.x))[0];
        if (target && Math.abs(target.x - unit.x) < unit.size + target.size + 14) {
          if (unit.cooldown <= 0) { target.hp -= unit.damage; unit.cooldown = 0.72; }
        } else {
          unit.x += (unit.side === 'player' ? 1 : -1) * unit.speed * dt;
          const atBase = unit.side === 'player' ? unit.x > 835 : unit.x < 125;
          if (atBase && unit.cooldown <= 0) {
            if (unit.side === 'player') game.enemyBase -= unit.damage * 0.34;
            else game.playerBase -= unit.damage * 0.34;
            unit.cooldown = 0.78;
          }
        }
      });
      const defeated = game.enemies.filter((unit) => unit.hp <= 0).length;
      if (defeated) { game.gold = Math.min(260, game.gold + defeated * 13); this.score += defeated * 55; }
      game.units = game.units.filter((unit) => unit.hp > 0 && unit.x < 860);
      game.enemies = game.enemies.filter((unit) => unit.hp > 0 && unit.x > 100);
      if (game.enemyBase <= 0) this.finish('Your guardians crossed the field and secured the final gate.', true);
      if (game.playerBase <= 0) this.finish('The gate fell. Build income, deploy earlier, and evolve when the lane is steady.');
    }

    drawAges() {
      const ctx = this.ctx;
      this.gradient('#163c31', '#ead7a4');
      ctx.fillStyle = 'rgba(255,240,190,.22)'; ctx.beginPath(); ctx.arc(770, 105, 62, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#3e6b50'; ctx.beginPath(); ctx.moveTo(0, 355); ctx.lineTo(200, 225); ctx.lineTo(380, 355); ctx.lineTo(600, 190); ctx.lineTo(960, 355); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#d1bc83'; ctx.fillRect(0, 390, WIDTH, 150);
      ctx.fillStyle = '#173d31'; ctx.fillRect(0, 430, WIDTH, 110);
      this.rounded(44, 276, 92, 162, 10, '#102c24', '#d9bd75');
      this.rounded(824, 276, 92, 162, 10, '#4d2028', '#d9bd75');
      this.label('F', 90, 344, 46, '#eddba9', 'center', 750);
      this.label('X', 870, 344, 34, '#f4d5d9', 'center', 750);
      this.health(40, 252, 104, this.ages.playerBase, 100, '#69c59b');
      this.health(816, 252, 104, this.ages.enemyBase, 100, '#db6876');
      [...this.ages.units, ...this.ages.enemies].forEach((unit) => {
        ctx.fillStyle = 'rgba(0,0,0,.22)'; ctx.beginPath(); ctx.ellipse(unit.x, 435, unit.size * 1.05, 7, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = unit.color; ctx.beginPath(); ctx.arc(unit.x, 408, unit.size, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#f7e5b2'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(unit.x, 420); ctx.lineTo(unit.x + (unit.side === 'player' ? 22 : -22), 394); ctx.stroke();
        this.health(unit.x - 20, 376, 40, unit.hp, unit.maxHp, unit.side === 'player' ? '#74d3a8' : '#e46d79');
        this.label(this.ages.age === 1 ? 'I' : this.ages.age === 2 ? 'II' : 'III', unit.x, 408, 11, '#071b15', 'center', 850);
      });
      this.rounded(18, 18, 290, 58, 14, 'rgba(4,21,16,.72)', 'rgba(255,255,255,.14)');
      this.label(`Gold ${Math.floor(this.ages.gold)}`, 38, 48, 18, '#f0d58e');
      this.label(`Deploy ${20 + this.ages.age * 6}`, 166, 48, 14, '#fff');
      this.label(this.ages.age < 3 ? `Evolve ${105 + this.ages.age * 55}` : 'Final era', 288, 48, 14, '#9fe1c3', 'right');
    }

    resetRunner() {
      this.runner = { player: { x: 150, y: 404, w: 42, h: 46, vy: 0 }, obstacles: [], coins: [], spawn: 0.8, coinSpawn: 1.2, speed: 270, distance: 0 };
    }

    jumpRunner() { if (this.runner.player.y >= 403) this.runner.player.vy = -570; }

    updateRunner(dt) {
      const game = this.runner;
      const player = game.player;
      if (this.controls.has('left')) player.x -= 260 * dt;
      if (this.controls.has('right')) player.x += 260 * dt;
      player.x = clamp(player.x, 40, 500);
      player.vy += 1450 * dt; player.y += player.vy * dt;
      if (player.y > 404) { player.y = 404; player.vy = 0; }
      game.distance += dt * game.speed / 24;
      game.speed = Math.min(520, 270 + game.distance * 1.2);
      game.spawn -= dt; game.coinSpawn -= dt;
      if (game.spawn <= 0) { game.obstacles.push({ x: 1020, y: 414, w: random(32, 55), h: random(38, 76) }); game.spawn = random(0.9, 1.6) * 300 / game.speed; }
      if (game.coinSpawn <= 0) { game.coins.push({ x: 1000, y: random(260, 390), r: 13 }); game.coinSpawn = random(0.8, 1.6); }
      game.obstacles.forEach((item) => { item.x -= game.speed * dt; });
      game.coins.forEach((item) => { item.x -= game.speed * dt; });
      const hit = game.obstacles.some((item) => player.x < item.x + item.w && player.x + player.w > item.x && player.y < item.y + item.h && player.y + player.h > item.y);
      if (hit) { this.score = Math.floor(game.distance * 10); this.finish(`You traveled ${Math.floor(game.distance)} meters. Time the next jump a little earlier.`); return; }
      game.coins = game.coins.filter((coin) => {
        const collected = Math.hypot(player.x + player.w / 2 - coin.x, player.y + player.h / 2 - coin.y) < 34;
        if (collected) this.score += 75;
        return !collected && coin.x > -30;
      });
      game.obstacles = game.obstacles.filter((item) => item.x > -100);
      this.score += dt * 8;
    }

    drawRunner() {
      const ctx = this.ctx; this.gradient('#234b5d', '#efca88');
      ctx.fillStyle = 'rgba(255,255,255,.18)'; for (let i = 0; i < 9; i += 1) ctx.fillRect(i * 125 - (this.runner.distance * 9) % 125, 250 - (i % 3) * 24, 70, 190 + (i % 3) * 24);
      ctx.fillStyle = '#173c31'; ctx.fillRect(0, 450, WIDTH, 90);
      this.runner.obstacles.forEach((item) => { this.rounded(item.x, item.y, item.w, item.h, 6, '#6e2934', '#f2c687'); });
      this.runner.coins.forEach((coin) => { ctx.fillStyle = '#f2d17d'; ctx.beginPath(); ctx.arc(coin.x, coin.y, coin.r, 0, Math.PI * 2); ctx.fill(); this.label('F', coin.x, coin.y + 1, 12, '#173c31', 'center', 850); });
      const p = this.runner.player; this.rounded(p.x, p.y, p.w, p.h, 12, '#d7b760', '#fff1c2');
      ctx.fillStyle = '#7a3440'; ctx.beginPath(); ctx.moveTo(p.x + 8, p.y); ctx.lineTo(p.x + p.w / 2, p.y - 18); ctx.lineTo(p.x + p.w - 8, p.y); ctx.closePath(); ctx.fill();
      this.label(`${Math.floor(this.runner.distance)}m`, 920, 42, 22, '#fff', 'right', 780);
    }

    resetHoops() { this.hoops = { meter: 0.08, dir: 1, target: random(0.28, 0.72), shots: 0, makes: 0, anim: -1, made: false, accuracy: 0 }; }

    shootHoops() {
      if (this.hoops.anim >= 0) return;
      this.hoops.accuracy = Math.abs(this.hoops.meter - this.hoops.target);
      this.hoops.made = this.hoops.accuracy < 0.105;
      this.hoops.anim = 0;
      if (this.hoops.made) {
        this.hoops.makes += 1;
        this.score += this.hoops.accuracy < 0.035 ? 300 : 200;
      }
    }

    updateHoops(dt) {
      const game = this.hoops;
      if (game.anim < 0) {
        game.meter += game.dir * dt * 0.7;
        if (game.meter > 1 || game.meter < 0) { game.dir *= -1; game.meter = clamp(game.meter, 0, 1); }
      } else {
        game.anim += dt * 0.9;
        if (game.anim > 1.25) {
          game.shots += 1; game.anim = -1; game.target = random(0.22, 0.78);
          if (game.shots >= 10) this.finish(`You made ${game.makes} scoring releases across ten attempts.`, true);
        }
      }
    }

    drawHoops() {
      const ctx = this.ctx; this.gradient('#102c3f', '#d69d62');
      ctx.fillStyle = '#b76d3f'; ctx.fillRect(0, 350, WIDTH, 190);
      ctx.strokeStyle = 'rgba(255,255,255,.65)'; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(480, 540, 270, Math.PI, 0); ctx.stroke();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 8; ctx.strokeRect(720, 120, 150, 105); ctx.fillStyle = '#d65e42'; ctx.fillRect(690, 225, 120, 9);
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(750, 235, 38, 0, Math.PI); ctx.stroke();
      let bx = 210; let by = 386;
      if (this.hoops.anim >= 0) { const t = clamp(this.hoops.anim, 0, 1); bx = 210 + 540 * t; by = 386 - Math.sin(Math.PI * t) * (this.hoops.made ? 265 : 210); }
      ctx.fillStyle = '#e88336'; ctx.beginPath(); ctx.arc(bx, by, 21, 0, Math.PI * 2); ctx.fill();
      this.rounded(160, 44, 640, 28, 14, 'rgba(4,17,25,.62)');
      this.rounded(160 + this.hoops.target * 600, 47, 52, 22, 11, '#d8bb70');
      ctx.fillStyle = '#fff'; ctx.fillRect(178 + this.hoops.meter * 604, 40, 5, 36);
      this.label('Release inside gold', 480, 95, 14, 'rgba(255,255,255,.75)', 'center');
    }

    resetGoal() { this.goal = { aim: -0.85, dir: 1, keeper: random(-0.75, 0.75), shots: 0, goals: 0, anim: -1, goal: false, shotAim: 0 }; }

    shootGoal() {
      if (this.goal.anim >= 0) return;
      this.goal.shotAim = this.goal.aim;
      this.goal.goal = Math.abs(this.goal.aim - this.goal.keeper) > 0.28;
      this.goal.anim = 0;
      if (this.goal.goal) {
        this.goal.goals += 1;
        this.score += Math.abs(this.goal.aim) > 0.62 ? 180 : 120;
      }
    }

    updateGoal(dt) {
      const game = this.goal;
      if (game.anim < 0) {
        game.aim += game.dir * dt * 1.15;
        if (Math.abs(game.aim) > 0.92) { game.dir *= -1; game.aim = clamp(game.aim, -0.92, 0.92); }
      } else {
        game.anim += dt;
        if (game.anim > 1.15) {
          game.shots += 1; game.anim = -1; game.keeper = random(-0.78, 0.78);
          if (game.shots >= 8) this.finish(`You finished eight kicks with ${game.goals} goals.`, true);
        }
      }
    }

    drawGoal() {
      const ctx = this.ctx; this.gradient('#2c7650', '#153f2d');
      ctx.strokeStyle = 'rgba(255,255,255,.72)'; ctx.lineWidth = 5; ctx.strokeRect(170, 75, 620, 250);
      for (let x = 190; x < 790; x += 42) { ctx.beginPath(); ctx.moveTo(x, 75); ctx.lineTo(x, 325); ctx.strokeStyle = 'rgba(255,255,255,.13)'; ctx.lineWidth = 1; ctx.stroke(); }
      for (let y = 95; y < 325; y += 35) { ctx.beginPath(); ctx.moveTo(170, y); ctx.lineTo(790, y); ctx.stroke(); }
      const keeperX = 480 + this.goal.keeper * 245; this.rounded(keeperX - 27, 238, 54, 72, 15, '#c8a450', '#fff3c8');
      ctx.strokeStyle = '#fff3c8'; ctx.lineWidth = 8; ctx.beginPath(); ctx.moveTo(keeperX - 22, 258); ctx.lineTo(keeperX - 58, 235); ctx.moveTo(keeperX + 22, 258); ctx.lineTo(keeperX + 58, 235); ctx.stroke();
      const targetX = 480 + this.goal.aim * 275; ctx.strokeStyle = '#f0d585'; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(targetX, 180, 22, 0, Math.PI * 2); ctx.stroke();
      let ballX = 480; let ballY = 472;
      if (this.goal.anim >= 0) { const t = clamp(this.goal.anim, 0, 1); ballX = 480 + this.goal.shotAim * 275 * t; ballY = 472 - 292 * t; }
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(ballX, ballY, 18, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#17251f'; ctx.lineWidth = 3; ctx.stroke();
      this.label(this.goal.anim >= 0 ? (this.goal.goal ? 'GOAL' : 'SAVED') : 'Place the kick', 480, 38, 24, '#fff', 'center', 800);
    }

    resetBreakout() {
      const bricks = [];
      for (let row = 0; row < 5; row += 1) for (let col = 0; col < 10; col += 1) bricks.push({ x: 72 + col * 82, y: 60 + row * 42, w: 72, h: 28, alive: true, row });
      this.breakout = { paddleX: 480, ball: { x: 480, y: 447, vx: 260, vy: -300, r: 10, stuck: true }, bricks, lives: 3 };
    }

    launchBreakout() { this.breakout.ball.stuck = false; }

    updateBreakout(dt) {
      const game = this.breakout;
      if (this.controls.has('left')) game.paddleX -= 430 * dt;
      if (this.controls.has('right')) game.paddleX += 430 * dt;
      game.paddleX = clamp(game.paddleX, 70, 890);
      const ball = game.ball;
      if (ball.stuck) { ball.x = game.paddleX; return; }
      ball.x += ball.vx * dt; ball.y += ball.vy * dt;
      if (ball.x < ball.r || ball.x > WIDTH - ball.r) { ball.vx *= -1; ball.x = clamp(ball.x, ball.r, WIDTH - ball.r); }
      if (ball.y < ball.r) { ball.vy = Math.abs(ball.vy); ball.y = ball.r; }
      if (ball.vy > 0 && ball.y + ball.r > 468 && ball.y < 490 && Math.abs(ball.x - game.paddleX) < 75) {
        ball.vy = -Math.abs(ball.vy) * 1.015; ball.vx += (ball.x - game.paddleX) * 3.2;
      }
      game.bricks.forEach((brick) => {
        if (!brick.alive) return;
        if (ball.x + ball.r > brick.x && ball.x - ball.r < brick.x + brick.w && ball.y + ball.r > brick.y && ball.y - ball.r < brick.y + brick.h) {
          brick.alive = false; ball.vy *= -1; this.score += 20 + (5 - brick.row) * 5;
        }
      });
      if (ball.y > HEIGHT + 20) {
        game.lives -= 1;
        if (game.lives <= 0) { this.finish('The final ball slipped past the paddle. Keep the paddle centered under the rebound.'); return; }
        Object.assign(ball, { x: game.paddleX, y: 447, vx: random(-280, 280) || 220, vy: -310, stuck: true });
      }
      if (game.bricks.every((brick) => !brick.alive)) this.finish('Every gold block is clear. The wall is open.', true);
    }

    drawBreakout() {
      const ctx = this.ctx; this.gradient('#071b25', '#163e31');
      this.breakout.bricks.forEach((brick) => { if (brick.alive) this.rounded(brick.x, brick.y, brick.w, brick.h, 7, ['#d9bd72', '#bf8f42', '#6bad8c', '#568c78', '#7d4350'][brick.row], 'rgba(255,255,255,.18)'); });
      this.rounded(this.breakout.paddleX - 70, 468, 140, 18, 9, '#f0d68c', '#fff');
      const ball = this.breakout.ball; ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2); ctx.fill();
      if (ball.stuck) this.label('Launch when ready', 480, 520, 15, 'rgba(255,255,255,.68)', 'center');
    }

    resetSnake() {
      this.snake = { cols: 24, rows: 13, body: [{ x: 9, y: 6 }, { x: 8, y: 6 }, { x: 7, y: 6 }], direction: 'right', next: 'right', apple: { x: 17, y: 6 }, timer: 0 };
    }

    snakeDirection(direction) {
      const opposite = { left: 'right', right: 'left', up: 'down', down: 'up' };
      if (opposite[direction] !== this.snake.direction) this.snake.next = direction;
    }

    placeSnakeApple() {
      let apple;
      do { apple = { x: Math.floor(random(0, this.snake.cols)), y: Math.floor(random(0, this.snake.rows)) }; }
      while (this.snake.body.some((part) => part.x === apple.x && part.y === apple.y));
      this.snake.apple = apple;
    }

    updateSnake(dt) {
      const game = this.snake; game.timer += dt;
      const interval = Math.max(0.065, 0.13 - game.body.length * 0.0015);
      if (game.timer < interval) return;
      game.timer = 0; game.direction = game.next;
      const head = { ...game.body[0] };
      if (game.direction === 'left') head.x -= 1; if (game.direction === 'right') head.x += 1; if (game.direction === 'up') head.y -= 1; if (game.direction === 'down') head.y += 1;
      if (head.x < 0 || head.x >= game.cols || head.y < 0 || head.y >= game.rows || game.body.some((part) => part.x === head.x && part.y === head.y)) { this.finish(`The trail reached ${game.body.length} links. Leave yourself a wider turning lane.`); return; }
      game.body.unshift(head);
      if (head.x === game.apple.x && head.y === game.apple.y) { this.score += 100; this.placeSnakeApple(); }
      else game.body.pop();
    }

    drawSnake() {
      const ctx = this.ctx; this.gradient('#081c17', '#123b2e');
      const ox = 96; const oy = 62; const cell = 32;
      ctx.strokeStyle = 'rgba(255,255,255,.05)'; ctx.lineWidth = 1;
      for (let x = 0; x <= this.snake.cols; x += 1) { ctx.beginPath(); ctx.moveTo(ox + x * cell, oy); ctx.lineTo(ox + x * cell, oy + this.snake.rows * cell); ctx.stroke(); }
      for (let y = 0; y <= this.snake.rows; y += 1) { ctx.beginPath(); ctx.moveTo(ox, oy + y * cell); ctx.lineTo(ox + this.snake.cols * cell, oy + y * cell); ctx.stroke(); }
      this.snake.body.forEach((part, index) => this.rounded(ox + part.x * cell + 3, oy + part.y * cell + 3, cell - 6, cell - 6, 8, index === 0 ? '#f0d37e' : '#5ab389'));
      const apple = this.snake.apple; ctx.fillStyle = '#b84a59'; ctx.beginPath(); ctx.arc(ox + apple.x * cell + cell / 2, oy + apple.y * cell + cell / 2, 11, 0, Math.PI * 2); ctx.fill();
      this.label('✦', ox + apple.x * cell + cell / 2, oy + apple.y * cell + cell / 2 + 1, 13, '#fff', 'center');
    }

    resetMemory() {
      const values = ['F', 'E', 'B', 'E₂', '✦', '♛', '◎', '◆'];
      const cards = [...values, ...values].sort(() => Math.random() - 0.5).map((value, index) => ({ value, index, shown: false, matched: false }));
      this.memory = { cards, first: null, second: null, lock: 0, moves: 0 };
    }

    flipMemoryAt(x, y) {
      const game = this.memory; if (game.lock > 0) return;
      const ox = 190; const oy = 54; const w = 132; const h = 92; const gap = 14;
      const col = Math.floor((x - ox) / (w + gap)); const row = Math.floor((y - oy) / (h + gap));
      if (col < 0 || col > 3 || row < 0 || row > 3) return;
      const card = game.cards[row * 4 + col]; if (!card || card.shown || card.matched) return;
      card.shown = true;
      if (!game.first) game.first = card;
      else {
        game.second = card; game.moves += 1;
        if (game.first.value === card.value) { game.first.matched = true; card.matched = true; game.first = null; game.second = null; this.score += 180; if (game.cards.every((item) => item.matched)) this.finish(`All eight pairs matched in ${game.moves} moves.`, true); }
        else game.lock = 0.72;
      }
    }

    updateMemory(dt) {
      const game = this.memory;
      if (game.lock > 0) { game.lock -= dt; if (game.lock <= 0) { if (game.first) game.first.shown = false; if (game.second) game.second.shown = false; game.first = null; game.second = null; } }
    }

    drawMemory() {
      this.gradient('#1e1731', '#4b2850');
      const ox = 190; const oy = 54; const w = 132; const h = 92; const gap = 14;
      this.memory.cards.forEach((card, index) => {
        const col = index % 4; const row = Math.floor(index / 4); const x = ox + col * (w + gap); const y = oy + row * (h + gap);
        this.rounded(x, y, w, h, 14, card.matched ? '#356d5a' : card.shown ? '#e2c77f' : '#35203f', card.shown || card.matched ? '#fff0bd' : 'rgba(255,255,255,.18)');
        this.label(card.shown || card.matched ? card.value : '✶', x + w / 2, y + h / 2, card.shown || card.matched ? 28 : 21, card.shown ? '#27162f' : '#d7bd80', 'center', 800);
      });
    }

    resetLake() { this.lake = { player: { x: 480, y: 430, r: 22 }, hazards: [], stars: [], hazardTimer: 0.6, starTimer: 1, lives: 3, elapsed: 0 }; }

    updateLake(dt) {
      const game = this.lake; const p = game.player; const speed = 320;
      if (this.controls.has('left')) p.x -= speed * dt; if (this.controls.has('right')) p.x += speed * dt; if (this.controls.has('up')) p.y -= speed * dt; if (this.controls.has('down')) p.y += speed * dt;
      p.x = clamp(p.x, 28, 932); p.y = clamp(p.y, 70, 505); game.elapsed += dt; game.hazardTimer -= dt; game.starTimer -= dt;
      if (game.hazardTimer <= 0) { game.hazards.push({ x: random(30, 930), y: -30, r: random(15, 26), speed: random(170, 270) + game.elapsed * 2 }); game.hazardTimer = random(0.42, 0.82); }
      if (game.starTimer <= 0) { game.stars.push({ x: random(30, 930), y: -20, r: 12, speed: random(120, 175) }); game.starTimer = random(0.9, 1.45); }
      game.hazards.forEach((item) => { item.y += item.speed * dt; }); game.stars.forEach((item) => { item.y += item.speed * dt; });
      game.hazards = game.hazards.filter((item) => {
        const hit = Math.hypot(p.x - item.x, p.y - item.y) < p.r + item.r;
        if (hit) { game.lives -= 1; p.x = 480; p.y = 430; if (game.lives <= 0) this.finish(`You stayed in the current for ${Math.floor(game.elapsed)} seconds. Watch the upper waterline for the next opening.`); }
        return !hit && item.y < 590;
      });
      game.stars = game.stars.filter((item) => { const caught = Math.hypot(p.x - item.x, p.y - item.y) < p.r + item.r; if (caught) this.score += 120; return !caught && item.y < 570; });
      this.score += dt * 6;
      if (game.elapsed >= 45) this.finish('You navigated the full current and reached open water.', true);
    }

    drawLake() {
      const ctx = this.ctx; this.gradient('#123f58', '#1a7784');
      ctx.strokeStyle = 'rgba(255,255,255,.12)'; ctx.lineWidth = 2;
      for (let y = 60; y < HEIGHT; y += 56) { ctx.beginPath(); for (let x = 0; x <= WIDTH; x += 32) ctx.lineTo(x, y + Math.sin((x + this.lake.elapsed * 70) / 80) * 8); ctx.stroke(); }
      this.lake.hazards.forEach((item) => { ctx.fillStyle = '#7b3544'; ctx.beginPath(); ctx.arc(item.x, item.y, item.r, 0, Math.PI * 2); ctx.fill(); this.label('×', item.x, item.y, 16, '#f4d9dc', 'center', 800); });
      this.lake.stars.forEach((item) => { ctx.fillStyle = '#f0d078'; ctx.beginPath(); ctx.arc(item.x, item.y, item.r, 0, Math.PI * 2); ctx.fill(); this.label('✦', item.x, item.y + 1, 12, '#123f58', 'center', 800); });
      const p = this.lake.player; ctx.fillStyle = '#f4e7bd'; ctx.beginPath(); ctx.moveTo(p.x, p.y - 25); ctx.lineTo(p.x - 22, p.y + 20); ctx.lineTo(p.x + 22, p.y + 20); ctx.closePath(); ctx.fill(); ctx.fillStyle = '#b44152'; ctx.fillRect(p.x - 3, p.y - 17, 6, 30);
    }

    resetTower() { this.tower = { blocks: [{ x: 260, y: 492, w: 440, h: 32 }], current: { x: 0, y: 452, w: 350, h: 32, dir: 1, speed: 220 } }; }

    dropTower() {
      const game = this.tower; const last = game.blocks[game.blocks.length - 1]; const current = game.current;
      const left = Math.max(last.x, current.x); const right = Math.min(last.x + last.w, current.x + current.w); const overlap = right - left;
      if (overlap <= 5) { this.finish(`The tower reached ${game.blocks.length - 1} floors. Center each new floor over the one below.`); return; }
      game.blocks.push({ x: left, y: current.y, w: overlap, h: current.h }); this.score += Math.round(overlap);
      if (game.blocks.length >= 18) { this.finish('The Tower of Purpose reached its full height.', true); return; }
      let nextY = current.y - 38;
      if (nextY < 78) { game.blocks.forEach((block) => { block.y += 38; }); nextY += 38; }
      game.current = { x: current.dir > 0 ? 0 : WIDTH - overlap, y: nextY, w: overlap, h: 32, dir: current.dir * -1, speed: Math.min(520, current.speed + 22) };
    }

    updateTower(dt) {
      const current = this.tower.current; current.x += current.dir * current.speed * dt;
      if (current.x < 0 || current.x + current.w > WIDTH) { current.dir *= -1; current.x = clamp(current.x, 0, WIDTH - current.w); }
    }

    drawTower() {
      const ctx = this.ctx; this.gradient('#1e3044', '#d1a768');
      ctx.fillStyle = 'rgba(255,255,255,.1)'; for (let i = 0; i < 20; i += 1) ctx.fillRect(i * 55, 95 + (i % 5) * 18, 2, 320);
      [...this.tower.blocks, this.tower.current].forEach((block, index) => this.rounded(block.x, block.y, block.w, block.h, 6, index === this.tower.blocks.length ? '#67b49a' : index % 2 ? '#d5b566' : '#9d7440', 'rgba(255,255,255,.22)'));
      this.label(`${this.tower.blocks.length - 1} floors`, 480, 38, 22, '#fff', 'center', 780);
    }

    resetFocus() { this.focus = { phase: 'waiting', timer: random(1.6, 3.8), rounds: 0, lives: 3, reaction: 0, message: 'Wait for green', readyAt: 0 }; }

    reactFocus() {
      const game = this.focus;
      if (game.phase === 'waiting') { game.lives -= 1; game.message = 'Too soon'; game.phase = 'result'; game.timer = 0.9; if (game.lives <= 0) this.finish('Three early reactions ended the round. Breathe, wait for green, then move.'); }
      else if (game.phase === 'ready') { game.reaction = Math.max(1, performance.now() - game.readyAt); game.message = `${Math.round(game.reaction)} ms`; this.score += Math.max(100, 1100 - game.reaction * 1.8); game.rounds += 1; game.phase = 'result'; game.timer = 1.1; if (game.rounds >= 5) this.finish(`Five clean reactions complete. Your final response was ${Math.round(game.reaction)} milliseconds.`, true); }
    }

    updateFocus(dt) {
      const game = this.focus; game.timer -= dt;
      if (game.timer > 0) return;
      if (game.phase === 'waiting') { game.phase = 'ready'; game.readyAt = performance.now(); game.message = 'REACT'; }
      else if (game.phase === 'result') { game.phase = 'waiting'; game.timer = random(1.5, 3.8); game.message = 'Wait for green'; }
    }

    drawFocus() {
      const game = this.focus;
      const colors = game.phase === 'ready' ? ['#0f8a55', '#26ba72'] : game.phase === 'result' ? ['#74404b', '#9c5d65'] : ['#102a24', '#173e33'];
      this.gradient(colors[0], colors[1]);
      this.ctx.fillStyle = 'rgba(255,255,255,.06)'; this.ctx.beginPath(); this.ctx.arc(480, 270, 150, 0, Math.PI * 2); this.ctx.fill();
      this.label(game.message, 480, 260, game.phase === 'ready' ? 72 : 48, '#fff', 'center', 850);
      this.label(`${game.lives} chances · ${game.rounds}/5 clean`, 480, 340, 18, 'rgba(255,255,255,.7)', 'center');
    }

    resetMerge() { this.merge = { grid: Array(16).fill(0) }; this.addMergeTile(); this.addMergeTile(); }

    addMergeTile() {
      const empty = this.merge.grid.map((value, index) => value ? -1 : index).filter((index) => index >= 0);
      if (!empty.length) return;
      this.merge.grid[empty[Math.floor(Math.random() * empty.length)]] = Math.random() < 0.88 ? 1 : 2;
    }

    mergeLine(values) {
      const packed = values.filter(Boolean); const result = [];
      for (let index = 0; index < packed.length; index += 1) {
        if (packed[index] === packed[index + 1]) { const merged = packed[index] * 2; result.push(merged); this.score += merged * 10; index += 1; }
        else result.push(packed[index]);
      }
      while (result.length < 4) result.push(0);
      return result;
    }

    moveMerge(direction) {
      if (!this.running) return;
      const before = this.merge.grid.join(','); const next = Array(16).fill(0);
      for (let line = 0; line < 4; line += 1) {
        const indexes = direction === 'left' ? [line * 4, line * 4 + 1, line * 4 + 2, line * 4 + 3]
          : direction === 'right' ? [line * 4 + 3, line * 4 + 2, line * 4 + 1, line * 4]
          : direction === 'up' ? [line, line + 4, line + 8, line + 12]
          : [line + 12, line + 8, line + 4, line];
        const merged = this.mergeLine(indexes.map((index) => this.merge.grid[index]));
        indexes.forEach((index, offset) => { next[index] = merged[offset]; });
      }
      if (next.join(',') === before) return;
      this.merge.grid = next; this.addMergeTile(); this.draw(); this.updateHud();
      const full = this.merge.grid.every(Boolean);
      const canMerge = this.merge.grid.some((value, index) => (index % 4 < 3 && value === this.merge.grid[index + 1]) || (index < 12 && value === this.merge.grid[index + 4]));
      if (Math.max(...this.merge.grid) >= 256) this.finish('Find, Establish, Believe, and Experience merged into a complete FEBE field.', true);
      else if (full && !canMerge) this.finish(`The board closed at ${Math.max(...this.merge.grid)}. Protect open corners and build one direction at a time.`);
    }

    drawMerge() {
      this.gradient('#172a43', '#315d82');
      const ox = 250; const oy = 42; const size = 102; const gap = 12;
      const labels = { 1: 'F', 2: 'E', 4: 'B', 8: 'E', 16: '16', 32: '32', 64: '64', 128: '128', 256: 'FEBE' };
      const colors = { 0: 'rgba(255,255,255,.08)', 1: '#e8ddbd', 2: '#d8c079', 4: '#76b69b', 8: '#b35a64', 16: '#4d8e85', 32: '#9b6c3d', 64: '#6e4e83', 128: '#2b6f67', 256: '#d7b65d' };
      this.merge.grid.forEach((value, index) => {
        const x = ox + (index % 4) * (size + gap); const y = oy + Math.floor(index / 4) * (size + gap);
        this.rounded(x, y, size, size, 15, colors[value] || '#d7b65d', 'rgba(255,255,255,.15)');
        if (value) this.label(labels[value] || value, x + size / 2, y + size / 2, value >= 128 ? 22 : 30, value <= 2 ? '#173044' : '#fff', 'center', 850);
      });
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-febe-arcade]').forEach((root) => {
      if (root.dataset.febeArcadeReady === 'true') return;
      root.dataset.febeArcadeReady = 'true';
      root.febeArcade = new FebeArcade(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-febe-arcade]').forEach((root) => {
      root.febeArcade?.abortController?.abort();
      root.febeArcade?.close();
      delete root.febeArcade;
    });
  });
})();
